<script>
import {functions} from '@src/db';
import * as GeoUtils from '@common/geo_utils';

export default {
  name: 'FcmCityAutocomplete',
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: [Object],
      default: ()=>{return {};},
    },
    label: {
      type: String,
      default: null,
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      searchDebouncedFn: null,
      lastSearchTime: null,
      inputModel: '',
      cityList: [],
      suppressNextSearch: false,
    };
  },
  computed: {
    // VAutocomplete resolves what to display by matching this value's item-value (place_id,
    // see template) against entries in :items — it does NOT just render whatever text/value
    // is handed to it. If no match is found in the list it silently shows blank, which is why
    // the watcher below also seeds cityList with this same entry.
    selectedItem() {
      if (!this.value || !this.value.city) {
        return null;
      }
      return {
        formatted_address: this.describeValue(this.value),
        place_id: this.value.placeId || null,
      };
    },
  },
  watch: {
    selectedItem: {
      immediate: true,
      handler(item) {
        if (item && !this.cityList.some(i => i.place_id === item.place_id)) {
          this.cityList = [item, ...this.cityList];
        }
      },
    },
    inputModel(value) {
      // VAutocomplete rewrites the search input to the selected item's text right
      // after citySelect() runs; skip that one change so it doesn't trigger a
      // redundant (and sometimes failing) search right after a successful pick.
      if (this.suppressNextSearch) {
        this.suppressNextSearch = false;
        return;
      }
      this.searchDebouncedFn();
    },
  },
  mounted: async function() {
    // Same Geocoding API used server-side by voxspot.f.ts (mapClient.geocode); a single
    // call already returns full address_components + geometry, so no separate "details"
    // round-trip is needed once a result is picked (see citySelect below).
    this.searchDebouncedFn = _.debounce(async ()=>{
      try {
        if(!this.inputModel) {
          return null;
        }
        const time = Date.now();
        this.lastSearchTime = time;
        const result = await functions.httpsCallable('maps-geocodeCity')({
          term: this.inputModel,
          language: this.$i18n.locale,
        });
        if (this.lastSearchTime !== time) { // ignore that search
          return null;
        }
        this.cityList = result.data || [];
      } catch(error) {
        console.error('geocodeCity', error);
        this.$showError({title: this.$t('form.error.listAddressError')});
      }
    }, 500);
  },
  methods: {
    describeValue(value) {
      if (!value || !value.city) {
        return '';
      }
      return [value.city, value.state, value.country].filter(Boolean).join(', ');
    },
    citySelect(input) {
      this.suppressNextSearch = true;
      if (!input || !input.place_id) {
        this.$emit('input', null);
        return;
      }
      const parsed = GeoUtils.googleAddress2db(input);
      // Keep only the fields relevant to a city-level pick (no street/number/complement/
      // zipcode/neighborhood), matching the shape already written by the mobile app's
      // registration flow.
      this.$emit('input', {
        city: parsed.city || '',
        state: parsed.state || '',
        country: parsed.country || '',
        lat: parsed.lat || 0,
        lng: parsed.lng || 0,
        placeId: parsed.placeId || '',
        name: parsed.city || '',
      });
    },
  },
};
</script>

<template>
  <VAutocomplete
      :class="$style.inputField"
      :label="label || $t('form.address.city')"
      :placeholder="$t('form.address.searchCity')"
      :items="cityList"
      :value="selectedItem"
      :search-input.sync="inputModel"
      :rules="rules"
      item-text="formatted_address"
      :item-value="item => item.place_id"
      hide-no-data
      no-filter
      return-object
      clearable
      append-outer-icon="mdi-map-marker"
      autocomplete="no"
      @input="citySelect"
  />
</template>

<style lang="scss" module>
.inputField {
  min-width: 200px;
}
</style>
